import React, { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import { fetchUserProfile, fetchGraphUserProfile } from '../services/userService';
import { idleTimeoutService } from '../services/idleTimeoutService';
import { AuthContext } from './AuthContextDefinition';
import type { AuthContextType, User } from './AuthContextDefinition';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [unauthorized, setUnauthorized] = useState(false);
    const [stage, setStage] = useState<'initializing' | 'authenticating' | 'loading_profile' | 'finalizing'>('initializing');
    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const [idleWarningSeconds, setIdleWarningSeconds] = useState(0);

    // Single initialization effect
    useEffect(() => {
        initializeAuthentication();
    }, [accounts, inProgress, instance]);

    // Idle timeout effect - initialize/stop based on user authentication status
    useEffect(() => {
        if (user && !unauthorized && !loading) {
            // User is authenticated - start idle timeout monitoring
            idleTimeoutService.initialize(
                instance,
                (secondsRemaining: number) => {
                    // Show warning modal
                    setShowIdleWarning(true);
                    setIdleWarningSeconds(secondsRemaining);
                },
                () => {
                    // Auto-logout callback
                    logout();
                }
            );
            console.info('[Auth] Idle timeout service started for authenticated user');
        } else {
            // User is not authenticated - stop idle timeout monitoring
            idleTimeoutService.stop();
        }

        // Cleanup on unmount
        return () => {
            idleTimeoutService.stop();
        };
    }, [user, unauthorized, loading, instance]);

    const initializeAuthentication = useCallback(async () => {
        // Wait for MSAL to complete any in-progress operations
        if (inProgress !== 'none') {
            return;
        }

        try {
            setLoginError(null);
            setUnauthorized(false);
            setStage('initializing');

            // Check if user is already authenticated
            if (accounts.length > 0) {
                instance.setActiveAccount(accounts[0]);
                setStage('authenticating');
                
                // Try to load cached user first for better UX
                const cachedUser = getCachedUser();
                if (cachedUser && isValidCachedUser(cachedUser)) {
                    setUser(cachedUser);
                    setStage('finalizing');
                    setLoading(false);
                    
                    // Validate in background without blocking UI
                    validateUserInBackground();
                } else {
                    // No cache, load fresh data
                    await loadUserData();
                }
            } else {
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                // No authenticated user, start login flow
                setLoading(false);
                await performLogin();
            }
        } catch (error) {
            console.error('Authentication initialization failed:', error);
            setLoginError('Authentication failed. Please try again.');
            setLoading(false);
        }
    }, [accounts, inProgress, instance]);

    const getCachedUser = (): User | null => {
        try {
            const cached = sessionStorage.getItem('bct_user');
            const cacheTimestamp = sessionStorage.getItem('bct_user_timestamp');
            
            if (!cached || !cacheTimestamp) {
                return null;
            }

            // Check if cache is expired (1 hour - aligned with session timeout)
            const cacheAge = Date.now() - parseInt(cacheTimestamp);
            const maxCacheAge = 60 * 60 * 1000; // 1 hour
            
            if (cacheAge > maxCacheAge) {
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                return null;
            }

            return JSON.parse(cached);
        } catch (error) {
            console.error('Failed to parse cached user:', error);
            sessionStorage.removeItem('bct_user');
            sessionStorage.removeItem('bct_user_timestamp');
            sessionStorage.removeItem('bct_last_validation');
            return null;
        }
    };

    const isValidCachedUser = (user: User): boolean => {
        return !!(user?.id && user?.name && user?.email);
    };

    const validateUserInBackground = async () => {
        try {
            // Only validate with backend occasionally to avoid excessive API calls
            const lastValidation = sessionStorage.getItem('bct_last_validation');
            const validationInterval = 5 * 60 * 1000; // 5 minutes
            
            if (lastValidation && (Date.now() - parseInt(lastValidation)) < validationInterval) {
                return; // Skip validation if done recently
            }

            // Silently validate with backend
            await fetchUserProfile();
            sessionStorage.setItem('bct_last_validation', Date.now().toString());
        } catch (error: any) {
            console.warn('Background validation failed:', error);
            
            // Check for 403 Forbidden - user not authorized in system
            if (error.response?.status === 403 || 
                error.message?.includes('not authorized') || 
                error.message?.includes('User not authorized')) {
                
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                setUnauthorized(true);
                setLoginError('You are not authorized to access this application.');
                
                // Store partial user info for unauthorized screen
                const account = instance.getActiveAccount();
                if (account) {
                    const unauthorizedUser: User = {
                        id: account.localAccountId,
                        name: account.name || 'User',
                        email: account.username,
                        jobTitle: 'Unauthorized User',
                    };
                    setUser(unauthorizedUser);
                }
            } 
            // Check for 401 Unauthorized - token expired
            else if (error.response?.status === 401 || error.message?.includes('Token expired')) {
                // Token expired - trigger re-authentication
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                await loadUserData();
            }
            // Other errors - try to refresh user data silently
            else {
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                await loadUserData().catch(() => {
                    // If refresh fails, don't block the user
                    console.warn('Silent user data refresh failed');
                });
            }
        }
    };

    const loadUserData = async () => {
        try {
            setLoginError(null);
            setUnauthorized(false);
            setStage('authenticating');
            
            // Step 1: Authenticate with backend (validates token and user permissions)
            await fetchUserProfile();
            
            // Step 2: Get complete profile from Microsoft Graph
            setStage('loading_profile');
            const graphUserData = await fetchGraphUserProfile();
            
            // Step 3: Cache and set user data with timestamp
            setStage('finalizing');
            setUser(graphUserData);
            sessionStorage.setItem('bct_user', JSON.stringify(graphUserData));
            sessionStorage.setItem('bct_user_timestamp', Date.now().toString());
            sessionStorage.setItem('bct_last_validation', Date.now().toString());
            
        } catch (error: any) {
            console.error('Failed to load user data:', error);
            
            // Handle 403 Forbidden - user not authorized in system (DO NOT trigger re-auth)
            if (error.response?.status === 403 || 
                error.message?.includes('not authorized') || 
                error.message?.includes('User not authorized')) {
                
                setUnauthorized(true);
                setLoginError('You are not authorized to access this application.');
                
                // Store partial user info for unauthorized screen display
                const account = instance.getActiveAccount();
                if (account) {
                    const unauthorizedUser: User = {
                        id: account.localAccountId,
                        name: account.name || 'User',
                        email: account.username,
                        jobTitle: 'Unauthorized User',
                    };
                    setUser(unauthorizedUser);
                }
                setLoading(false);
                return; // IMPORTANT: Stop here, don't try fallback or re-auth
            } 
            
            // Handle 401 Unauthorized - token expired (trigger re-auth)
            if (error.response?.status === 401 || error.message?.includes('Token expired')) {
                setLoginError('Your session has expired. Please sign in again.');
                sessionStorage.removeItem('bct_user');
                sessionStorage.removeItem('bct_user_timestamp');
                sessionStorage.removeItem('bct_last_validation');
                await performLogin();
                return;
            } 
            
            // Handle network errors
            if (error.message?.includes('network') || error.message?.includes('connection')) {
                setLoginError('Unable to connect to the server. Please check your internet connection and try again.');
            } 
            // Generic error
            else {
                setLoginError('Failed to load user information. Please try again.');
            }
            
            // Only try fallback for non-403 errors
            await createFallbackUser();
        } finally {
            setLoading(false);
        }
    };

    const createFallbackUser = async () => {
        const account = instance.getActiveAccount();
        if (!account) {
            setLoginError('Unable to retrieve account information. Please try logging in again.');
            return;
        }

        try {
            // Ensure backend authentication still works
            await fetchUserProfile();
            
            // Create minimal user profile from MSAL account
            const fallbackUser: User = {
                id: account.localAccountId,
                name: account.name || 'User',
                email: account.username,
                jobTitle: (account.idTokenClaims as any)?.jobTitle || 'User',
            };

            setUser(fallbackUser);
            sessionStorage.setItem('bct_user', JSON.stringify(fallbackUser));
            sessionStorage.setItem('bct_user_timestamp', Date.now().toString());
            setLoginError(null);
            console.log('Created fallback user profile');
            
        } catch (error: any) {
            console.error('Fallback user creation failed:', error);
            
            // Handle 403 specifically - DO NOT trigger re-auth
            if (error.response?.status === 403 || 
                error.message?.includes('not authorized') || 
                error.message?.includes('User not authorized')) {
                
                setUnauthorized(true);
                setLoginError('You are not authorized to access this application.');
                
                // Store partial user info for unauthorized screen
                const unauthorizedUser: User = {
                    id: account.localAccountId,
                    name: account.name || 'User',
                    email: account.username,
                    jobTitle: 'Unauthorized User',
                };
                setUser(unauthorizedUser);
            } else {
                // Other errors - trigger re-auth
                setLoginError('Failed to authenticate. Please try logging in again.');
                await performLogin();
            }
        }
    };

    const performLogin = async () => {
        try {
            setLoading(true);
            setLoginError(null);
            setUnauthorized(false);
            setStage('authenticating');
            
            await instance.loginRedirect(loginRequest);
        } catch (error: any) {
            console.error('Login failed:', error);
            
            let errorMessage = 'Login failed. Please try again.';
            
            if (error.errorCode === 'user_cancelled') {
                errorMessage = 'Login was cancelled. Please try again to access the application.';
            } else if (error.errorCode === 'popup_window_error') {
                errorMessage = 'Please allow popups for this site and try again.';
            } else if (error.errorCode === 'interaction_in_progress') {
                errorMessage = 'Authentication is already in progress. Please wait.';
            } else if (error.errorMessage) {
                errorMessage = error.errorMessage;
            }
            
            setLoginError(errorMessage);
            setLoading(false);
        }
    };

    const login = async () => {
        await performLogin();
    };

    const logout = () => {
        // Stop idle timeout monitoring
        idleTimeoutService.stop();
        
        // Clear all user data
        setUser(null);
        setLoginError(null);
        setUnauthorized(false);
        setStage('initializing');
        sessionStorage.removeItem('bct_user');
        sessionStorage.removeItem('bct_user_timestamp');
        sessionStorage.removeItem('bct_last_validation');
        
        // Logout from Azure AD
        instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin
        });
    };

    const retryLogin = async () => {
        // Clear unauthorized state and try fresh login
        setUnauthorized(false);
        setLoading(true);
        setLoginError(null);
        setStage('initializing');
        sessionStorage.removeItem('bct_user');
        sessionStorage.removeItem('bct_user_timestamp');
        sessionStorage.removeItem('bct_last_validation');
        await performLogin();
    };

    const refreshUserProfile = async () => {
        if (!user) {
            console.warn('No user to refresh');
            return;
        }
        
        try {
            setStage('loading_profile');
            const refreshedData = await fetchGraphUserProfile();
            
            setUser(refreshedData);
            sessionStorage.setItem('bct_user', JSON.stringify(refreshedData));
            sessionStorage.setItem('bct_user_timestamp', Date.now().toString());
            setStage('finalizing');
            
            console.log('User profile refreshed successfully');
        } catch (error: any) {
            console.error('Failed to refresh user profile:', error);
            
            // Handle 403 - set unauthorized state
            if (error.response?.status === 403 || 
                error.message?.includes('not authorized') || 
                error.message?.includes('User not authorized')) {
                setUnauthorized(true);
                setLoginError('You are not authorized to access this application.');
            }
        }
    };

    const contextValue: AuthContextType = {
        user,
        isAuthenticated: !!user && !loading && !unauthorized,
        login,
        logout,
        loading,
        loginError,
        retryLogin,
        refreshUserProfile,
        unauthorized,
        stage,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};